import { useEffect, useMemo, useRef, useState } from 'react';
import { api, parseErr } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { useFeature } from '@/lib/features';
import { useAuth } from '@/lib/auth';
import { MessageSquare, CheckCircle2, Quote as QuoteIcon, Sparkles, ClipboardCheck } from 'lucide-react';

// Small inline-comments panel for SOP knowledge pages.
// - Anyone can post a comment tied to a selected quote.
// - Page owner or Owner can resolve.
// - Gated by feature flag 'sop_comments'.
export function SopCommentsPanel({ page }) {
  const enabled = useFeature('sop_comments');
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [openCount, setOpenCount] = useState(0);
  const [tab, setTab] = useState('open');
  const [selectionQuote, setSelectionQuote] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);
  const [resolveTarget, setResolveTarget] = useState(null);
  const canResolve = useMemo(() => {
    if (!page) return false;
    if (user?.role === 'owner') return true;
    return page.owner_user_id && user?.id === page.owner_user_id;
  }, [page, user]);

  const load = async () => {
    if (!page) return;
    try {
      const r = await api.get(`/sop/pages/${page.id}/comments`);
      setComments(r.data.comments || []);
      setOpenCount(r.data.open_count || 0);
    } catch (e) { toast.error(parseErr(e)); }
  };
  useEffect(() => { load(); }, [page?.id]); // eslint-disable-line

  // Listen to text selection on the page canvas
  useEffect(() => {
    if (!enabled) return () => {};
    const onMouseUp = () => {
      const sel = window.getSelection && window.getSelection();
      const text = sel ? String(sel.toString() || '').trim() : '';
      // Only capture selections that are inside a page canvas
      if (text && text.length >= 3 && text.length <= 500) {
        // Check anchor node is within .page-canvas
        const anchor = sel.anchorNode;
        const inCanvas = anchor && (anchor.parentElement || anchor).closest && (anchor.parentElement || anchor).closest('.page-canvas');
        if (inCanvas) {
          setSelectionQuote(text);
          return;
        }
      }
      setSelectionQuote('');
    };
    document.addEventListener('mouseup', onMouseUp);
    document.addEventListener('keyup', onMouseUp);
    return () => {
      document.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('keyup', onMouseUp);
    };
  }, [enabled]);

  if (!enabled || !page) return null;

  const openCommentDialog = () => {
    setDialogOpen(true);
  };

  const submit = async () => {
    if (!body.trim() || !selectionQuote) return;
    setSaving(true);
    try {
      await api.post('/sop/comments', {
        page_id: page.id,
        quote: selectionQuote,
        body: body.trim(),
      });
      toast.success('Comment posted. The SOP owner has been notified.');
      setBody(''); setSelectionQuote(''); setDialogOpen(false);
      load();
    } catch (e) { toast.error(parseErr(e)); }
    finally { setSaving(false); }
  };

  const resolve = async () => {
    if (!resolveTarget) return;
    try {
      await api.post(`/sop/comments/${resolveTarget.id}/resolve`, {
        resolution_note: resolveTarget.resolution_note || null,
        accepted_edit: !!resolveTarget.accepted_edit,
      });
      toast.success('Comment resolved.');
      setResolveTarget(null);
      load();
    } catch (e) { toast.error(parseErr(e)); }
  };

  const filtered = comments.filter(c => tab === 'open' ? c.status === 'open' : c.status === 'resolved');

  return (
    <div className="mt-8 border-t pt-4" style={{ borderColor: 'hsl(var(--border))' }} data-testid="sop-comments-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <MessageSquare size={16} className="text-[hsl(var(--foreground-muted))]" />
          <h3 className="font-heading font-semibold">Inline comments</h3>
          <span className="text-[11.5px] text-[hsl(var(--foreground-subtle))]">
            · {openCount} open · {comments.length - openCount} resolved
          </span>
        </div>
        <div className="flex items-center gap-1">
          <Button size="sm" variant={tab === 'open' ? 'default' : 'outline'} onClick={() => setTab('open')} data-testid="sop-comments-tab-open">Open</Button>
          <Button size="sm" variant={tab === 'resolved' ? 'default' : 'outline'} onClick={() => setTab('resolved')} data-testid="sop-comments-tab-resolved">Resolved</Button>
        </div>
      </div>

      <div className="mt-2 text-[12px] text-[hsl(var(--foreground-muted))] rounded-md border p-2 bg-[hsl(var(--surface-1))]" style={{ borderColor: 'hsl(var(--border))' }}>
        <Sparkles size={11} className="inline mr-1 text-[hsl(var(--primary))]" />
        <b>Highlight any text</b> on this SOP and the &ldquo;Add comment&rdquo; button will appear here.
        {selectionQuote && (
          <>
            <div className="mt-2 flex items-center gap-2">
              <QuoteIcon size={11} className="text-[hsl(var(--foreground-subtle))]" />
              <span className="italic">&ldquo;{selectionQuote.slice(0, 140)}{selectionQuote.length > 140 ? '…' : ''}&rdquo;</span>
            </div>
            <Button size="sm" className="mt-2" onClick={openCommentDialog} data-testid="sop-comment-add-btn">
              <MessageSquare size={13} className="mr-1" />Add comment on this selection
            </Button>
          </>
        )}
      </div>

      <ul className="mt-3 space-y-2">
        {filtered.length === 0 && (
          <li className="text-[13px] text-[hsl(var(--foreground-muted))] py-4 text-center italic">
            {tab === 'open' ? 'No open comments. This SOP is quiet.' : 'No resolved comments yet.'}
          </li>
        )}
        {filtered.map(c => (
          <li key={c.id} className="card p-3" data-testid={`sop-comment-${c.id}`}>
            <div className="flex items-start gap-2">
              <QuoteIcon size={13} className="text-[hsl(var(--foreground-subtle))] mt-1 flex-none" />
              <div className="flex-1 min-w-0">
                <div className="text-[12px] italic text-[hsl(var(--foreground-muted))] border-l-2 pl-2" style={{ borderColor: 'hsl(var(--primary))' }}>
                  &ldquo;{c.quote}&rdquo;
                </div>
                <div className="text-[13.5px] mt-2">{c.body}</div>
                <div className="text-[11.5px] text-[hsl(var(--foreground-subtle))] mt-1">
                  {c.author_name || c.author_email} · {new Date(c.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                </div>
                {c.status === 'resolved' && (
                  <div className="text-[12px] text-[hsl(var(--success))] mt-1 flex items-start gap-1">
                    <CheckCircle2 size={12} className="mt-0.5 flex-none" />
                    <span>Resolved by {c.resolved_by}{c.accepted_edit ? ' · accepted as edit' : ''}{c.resolution_note ? ` — “${c.resolution_note}”` : ''}</span>
                  </div>
                )}
              </div>
              {c.status === 'open' && canResolve && (
                <Button size="sm" variant="outline" onClick={() => setResolveTarget({ ...c, resolution_note: '', accepted_edit: false })} data-testid={`sop-comment-resolve-${c.id}`}>
                  <ClipboardCheck size={13} className="mr-1" />Resolve
                </Button>
              )}
            </div>
          </li>
        ))}
      </ul>

      <Dialog open={dialogOpen} onOpenChange={o => !o && setDialogOpen(false)}>
        <DialogContent data-testid="sop-comment-dialog">
          <DialogHeader>
            <DialogTitle>Comment on selected text</DialogTitle>
            <DialogDescription>
              Post a suggestion, question, or correction. The SOP owner is notified.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <div className="text-[12px] text-[hsl(var(--foreground-muted))] mb-1">Quoted from the SOP</div>
              <div className="text-[12.5px] italic border-l-2 pl-2" style={{ borderColor: 'hsl(var(--primary))' }}>
                &ldquo;{selectionQuote}&rdquo;
              </div>
            </div>
            <div>
              <div className="text-[12px] text-[hsl(var(--foreground-muted))] mb-1">Your comment</div>
              <Textarea rows={4} value={body} onChange={e => setBody(e.target.value)} placeholder="e.g. This step is missing the Ops review before the send." data-testid="sop-comment-body" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={!body.trim() || !selectionQuote || saving} data-testid="sop-comment-submit">
              {saving ? 'Posting…' : 'Post comment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resolveTarget} onOpenChange={o => !o && setResolveTarget(null)}>
        <DialogContent data-testid="sop-comment-resolve-dialog">
          <DialogHeader>
            <DialogTitle>Resolve comment</DialogTitle>
            <DialogDescription>
              Add a short note so the commenter sees what happened. If you edited the SOP based on this feedback, tick the box.
            </DialogDescription>
          </DialogHeader>
          {resolveTarget && (
            <div className="space-y-3">
              <div>
                <div className="text-[12px] text-[hsl(var(--foreground-muted))] mb-1">Note (optional)</div>
                <Textarea rows={3} value={resolveTarget.resolution_note || ''}
                          onChange={e => setResolveTarget({ ...resolveTarget, resolution_note: e.target.value })}
                          data-testid="sop-comment-resolve-note" />
              </div>
              <label className="flex items-center gap-2 text-[13px]">
                <input type="checkbox" checked={!!resolveTarget.accepted_edit}
                       onChange={e => setResolveTarget({ ...resolveTarget, accepted_edit: e.target.checked })}
                       data-testid="sop-comment-accept-edit" />
                I accepted this as an edit to the SOP
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setResolveTarget(null)}>Cancel</Button>
            <Button onClick={resolve} data-testid="sop-comment-resolve-confirm">Resolve</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
